import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tour from 'reactour';
import { TimesheetTourApi } from './useTimesheetTour';
import { TourConfetti } from './TourConfetti';
import { InvoiceMascot, MascotExpression } from '../invoice-tour/invoice-mascot.component';
import './timesheet-tour.scss';

const clampGuide = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/* TimesheetTour
   -------------
   Thin wrapper around reactour's <Tour>. The page mounts this once
   and passes the API object from useTimesheetTour(). All step body
   text lives in timesheet-tour.steps.tsx; this file wraps each
   step's body with a controls bar (Back / Next / Skip / Exit / Finish)
   that reads navigation state from the api.

   Bubble button labels and the gate / predicate hints are translated
   inside this component (Phase C) so the chrome follows the active
   locale alongside the step body text. */
interface TimesheetTourProps {
  api: TimesheetTourApi;
}

const TourBubble: React.FC<{
  /* `body` is a function so the translation calls inside it are
     evaluated at every render of the bubble — when the user switches
     language, useTranslation triggers a re-render of TourBubble and
     `body(t)` is called with the NEW t, immediately swapping the
     visible text. (Previously body was pre-resolved JSX, which
     reactour cached internally and didn't refresh.) */
  body: (t: (key: string) => string) => React.ReactNode;
  api: TimesheetTourApi;
}> = ({ body, api }) => {
  const { t } = useTranslation();
  const showNext = !api.isEventGated && !api.isLastStep;
  const showFinish = api.isLastStep;

  // Tour-guide mascot — same character as the Create-Invoice / Manage-Invoice
  // tours. Eyes follow the cursor; expression reacts to the control buttons.
  const [guideMood, setGuideMood] = useState<MascotExpression>('idle');
  const guideEyesRef = useRef<SVGGElement | null>(null);
  const guideWrapRef = useRef<HTMLSpanElement | null>(null);
  const trackGuideEyes = (e: React.MouseEvent): void => {
    const g = guideWrapRef.current?.getBoundingClientRect();
    if (!g || !guideEyesRef.current) return;
    const ox = g.left + g.width / 2;
    const oy = g.top + g.height * 0.42;
    const dx = clampGuide((e.clientX - ox) / 220, -1, 1);
    const dy = clampGuide((e.clientY - oy) / 220, -1, 1);
    guideEyesRef.current.style.transform = `translate(${dx * 2.6}px, ${dy * 2.2}px)`;
  };
  const resetGuideEyes = (): void => {
    if (guideEyesRef.current) guideEyesRef.current.style.transform = 'translate(0px, 0px)';
  };

  // Two hint variants:
  //   • event-gated steps: "Perform the highlighted action to continue."
  //   • predicate-gated steps where canAdvance===false: "Fill in the
  //     highlighted field to continue."
  // View-only steps render no hint.
  const predicateBlocked = !api.isEventGated && !api.canAdvance && !api.isLastStep;

  /* Progress 0..1 — drives the width of the gradient progress bar fill
     under the step counter. `+1` so Step 1 already shows a small sliver
     of progress instead of an empty bar. */
  const progressFraction = (api.currentStep + 1) / api.steps.length;

  /* The `key` makes React re-mount the bubble on every step change,
     which re-triggers the CSS entrance animations defined in
     timesheet-tour.scss (`tts-bubble-enter` + the staggered content
     fade-ins). Without a changing key React would reuse the same DOM
     and only the text would swap silently. */
  return (
    <div className="tts-bubble" key={api.currentStep} onMouseMove={trackGuideEyes} onMouseLeave={resetGuideEyes}>
      {/* Close × — replaces the previous text glyph with a crisp SVG
          so the line weights are consistent at every zoom level and the
          violet/white colour reliably follows `currentColor`. The
          rotate-on-hover animation lives in the SCSS rule for
          `.tts-bubble__close`. */}
      <button
        type="button"
        className="tts-bubble__close"
        aria-label={t('TOUR_SKIP') as string}
        onClick={api.close}
        onMouseEnter={() => setGuideMood('sad')}
        onMouseLeave={() => setGuideMood('idle')}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M2 2L12 12M12 2L2 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Guide mascot + step counter — same character on every step, happy
          on the final (celebration) step. */}
      <div className="tts-bubble__head">
        <span className="tts-bubble__guide" ref={guideWrapRef} aria-hidden="true">
          <InvoiceMascot expression={api.isLastStep ? 'happy' : guideMood} eyesRef={guideEyesRef} />
        </span>
        <div className="tts-bubble__progress">
          {(t('TOUR_STEP_PROGRESS', {
            current: api.currentStep + 1,
            total: api.steps.length,
          }) as string)}
        </div>
      </div>

      {/* Thin progress bar under the step counter — visual sense of how
          much of the tour is left. Width is set inline; the gradient
          fill is themed by the SCSS rule. */}
      <div className="tts-bubble__progress-bar" aria-hidden>
        <div
          className="tts-bubble__progress-bar-fill"
          style={{ width: `${progressFraction * 100}%` }}
        />
      </div>

      <div className="tts-bubble__body">{body(t as (k: string) => string)}</div>

      {api.isEventGated && (
        <div className="tts-bubble__hint">{t('TOUR_GATE_HINT') as string}</div>
      )}
      {predicateBlocked && (
        <div className="tts-bubble__hint">{t('TOUR_PREDICATE_HINT') as string}</div>
      )}

      <div className="tts-bubble__controls">
        <button
          type="button"
          className="tts-btn tts-btn--ghost"
          onClick={api.back}
          disabled={api.isFirstStep}
          onMouseEnter={() => setGuideMood('confused')}
          onMouseLeave={() => setGuideMood('idle')}
        >
          {t('TOUR_BACK') as string}
        </button>

        {showNext && (
          <button
            type="button"
            className="tts-btn tts-btn--primary"
            onClick={api.next}
            disabled={!api.canAdvance}
            title={!api.canAdvance ? (t('TOUR_PREDICATE_HINT') as string) : undefined}
            onMouseEnter={() => setGuideMood('happy')}
            onMouseLeave={() => setGuideMood('idle')}
          >
            {t('TOUR_NEXT') as string}
          </button>
        )}

        {showFinish && (
          <button
            type="button"
            className="tts-btn tts-btn--primary"
            /* `finish` (NOT `close`) clears the saved step so a future
               Start Tour begins fresh at Step 1. `close()` is for ×
               and Skip — those preserve the step for resume. */
            onClick={api.finish}
            onMouseEnter={() => setGuideMood('happy')}
            onMouseLeave={() => setGuideMood('idle')}
          >
            {t('TOUR_FINISH') as string}
          </button>
        )}

        {!api.isLastStep && (
          <button
            type="button"
            className="tts-btn tts-btn--text"
            onClick={api.skip}
            onMouseEnter={() => setGuideMood('sad')}
            onMouseLeave={() => setGuideMood('idle')}
          >
            {t('TOUR_SKIP') as string}
          </button>
        )}
      </div>
    </div>
  );
};

export const TimesheetTour: React.FC<TimesheetTourProps> = ({ api }) => {
  /* Subscribe TimesheetTour itself to i18n changes so it re-renders
     and rebuilds reactourSteps on language switch. Without this,
     reactour caches the helper's element reference and the bubble
     body shows stale (English) text after switching to Swedish,
     even though TourBubble itself uses useTranslation internally. */
  const { i18n } = useTranslation();

  // Rebuild reactour's step list on every render — cheap (~15 small
  // JSX trees) and keeps each step's content closure pointing at the
  // live api so Back/Next/Skip always call the current callbacks.
  // The `key={i18n.language}` on TourBubble forces a fresh React
  // element reference for each language, so reactour replaces the
  // cached helper content even when only the language has changed.
  const reactourSteps = api.steps.map((s) => ({
    selector: s.selector,
    position: s.position,
    content: <TourBubble key={`${s.key}-${i18n.language}`} body={s.body} api={api} />,
  }));

  /* Reactour v1 only listens for `resize` to re-measure the highlight
     (see reactour.esm.js: `window.addEventListener('resize', ...)`).
     It does NOT watch for `scroll`, so if the user scrolls the page
     mid-step the highlight stays anchored at the OLD viewport position
     while the target element scrolls away from it.
     Pipe scroll → synthetic resize so reactour re-measures on every
     scroll while the tour is open. Throttled via requestAnimationFrame
     to a max of one re-measure per paint, which the resize debounce
     inside reactour (70ms) further smooths. The capture phase + true
     option catches scrolls inside any nested scrollable container in
     the page, not just the window. */
  useEffect(() => {
    if (!api.isOpen || typeof window === 'undefined') return;
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        window.dispatchEvent(new Event('resize'));
      });
    };
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [api.isOpen]);

  /* Manual scroll-into-view on every step change.

     THE BUG this solves (client feedback, mobile only, Step 2 in
     particular): when the tour advances to a step whose target is
     off-screen, the page didn't scroll — the user saw the bubble
     saying "click the Timesheet Record button" but the button
     itself remained above the viewport, so the mask hole was drawn
     at reactour's `safe(top - padding) → 0` clamped position (a
     misleading horizontal band at the wrong place). User had to
     manually scroll to see the correct highlight.

     WHY the previous approaches were unreliable:
     • Reactour's `scrollSmooth.to()` sets `context.scrollTop = X`
       on `window`, which is a no-op in modern browsers — its
       built-in scroll silently fails.
     • `window.scrollTo({top, behavior: 'auto'})` worked on desktop
       Chrome but was unreliable on some Android WebViews and iOS
       Safari builds we've seen in the wild.

     THE FIX: use the browser-native `scrollIntoView` with
     `scroll-margin-top` set on the target itself. `scrollIntoView`
     handles ALL scroll containers (window, nested, iframe) and is
     the ONE scroll API that works uniformly on every browser we
     ship to. `scroll-margin-top` is the modern hint that tells
     the browser "when scrolling this into view, leave N pixels
     above" — which is exactly what we need to keep the target
     clear of the sticky header AND below the top-docked bubble
     (when applicable). Set inline on each step's target so the
     effect is scoped to that one element and cleaned up when the
     next step activates. */
  useEffect(() => {
    if (!api.isOpen || typeof window === 'undefined') return;
    const step = api.steps[api.currentStep];
    if (!step) return;
    /* Centered steps (welcome / completion) target the body — no
       scrolling needed. */
    if (step.selector === 'body') return;

    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const topDockedKeys = ['price', 'submit', 'add-entry-week'];
    const isTopDocked = topDockedKeys.includes(step.key);

    /* Scroll strategy differs by viewport class:
         • Mobile (<= 640px): bubble is docked to the top or bottom
           of the viewport via CSS. Target must be scrolled to a
           specific band above/below the bubble — we use
           `block: 'start'` with a scroll-margin-top that carves out
           room for the sticky header + docked bubble.
         • Desktop / tablet (> 640px): bubble is auto-positioned by
           reactour NEXT TO the target (top/bottom/left/right of
           the highlighted element). If we scroll the target to
           `block: 'start'` there's no room above for the bubble
           and reactour falls back to an awkward corner placement.
           `block: 'center'` gives reactour space on ALL sides so
           it can pick the best anchor position naturally.

       Values, when mobile scroll-margin-top applies:
         • Bottom-docked bubble (default): 96px — clears the
           sticky page header (~60px) with breathing room.
         • Top-docked bubble (price / submit / add-entry-week):
           360px — clears the top-docked bubble (~340px + gap). */
    const scrollBlock: ScrollLogicalPosition = isMobile ? 'start' : 'center';
    const scrollMarginTop = isMobile ? (isTopDocked ? 360 : 96) : 0;

    let modifiedTarget: HTMLElement | null = null;
    let originalMarginTop = '';

    const scrollNow = () => {
      try {
        const target = document.querySelector(step.selector) as HTMLElement | null;
        if (!target || typeof target.scrollIntoView !== 'function') return;

        /* First time we successfully find the target: memoise its
           original scroll-margin-top so we can restore it in the
           cleanup below. Cache is per-effect-run so re-entrant
           retries only save the value once (from the first, real,
           original value). Skipped entirely on desktop where we
           don't touch scroll-margin-top at all. */
        if (isMobile && !modifiedTarget) {
          modifiedTarget = target;
          originalMarginTop = target.style.scrollMarginTop || '';
        }
        if (isMobile) {
          target.style.scrollMarginTop = `${scrollMarginTop}px`;
        }

        /* `behavior: 'auto'` (instant) so reactour's smooth-scroll
           race window can't override us mid-animation. `inline:
           'nearest'` avoids horizontal scrolling on wide layouts. */
        target.scrollIntoView({
          behavior: 'auto',
          block: scrollBlock,
          inline: 'nearest',
        });

        /* Dispatch resize IMMEDIATELY after scroll so reactour
           re-reads getBoundingClientRect and draws the mask hole at
           the target's NEW (in-viewport) position. Without this
           reactour keeps the stale rect from before the scroll and
           the mask hole ends up at `safe(negativeTop) → 0` — the
           misleading band the client reported. */
        window.dispatchEvent(new Event('resize'));
      } catch { /* invalid selector — silently ignore */ }
    };

    /* Multi-attempt schedule:
       • t=0 — fires synchronously so if the target is already in the
         DOM (common case) the scroll happens BEFORE the user sees
         the wrong-position mask flash.
       • t=200 — catches targets that reactour just painted this tick
         but weren't yet layed out.
       • t=500/900 — catches targets that appear after animations
         (add-customer-modal 180ms, submit modal 200ms, sidebar
         slide-in 380ms).
       Each attempt is idempotent — if the browser is already at the
       correct scroll position, scrollIntoView is a no-op. */
    scrollNow();
    const timers = [200, 500, 900].map((d) => window.setTimeout(scrollNow, d));

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      /* Restore the target's original scroll-margin-top so the CSS
         change doesn't leak to non-tour scroll behaviour (e.g. a
         later anchor link scrolling to the same element). */
      if (modifiedTarget) {
        modifiedTarget.style.scrollMarginTop = originalMarginTop;
      }
    };
  }, [api.isOpen, api.currentStep, api.steps]);

  /* Show celebration confetti the moment the user lands on the final
     ("Congratulations 🎉") step. Mounting/unmounting on `isLastStep`
     means going Back and returning re-runs the animation; closing the
     tour unmounts the canvas-free overlay so nothing leaks. */
  const showConfetti = api.isOpen && api.isLastStep;

  return (
    <>
    {showConfetti && <TourConfetti />}
    {/* Conditional mount — `key={...}` is not enough on its own because
        reactour v1 keeps an internal `current` step counter that does
        NOT reset when isOpen flips back to true. If we leave the Tour
        permanently mounted, finishing at Step 15 and starting again
        would render Step 15's content (reactour's internal current is
        still 14) while our progress counter — which reads from the
        live `api.currentStep` — shows "Step 1 / 15". Conditional
        mount on `api.isOpen` forces a brand new Tour instance per
        session, guaranteeing reactour's internal state matches ours. */}
    {api.isOpen && <Tour
      steps={reactourSteps}
      isOpen={api.isOpen}
      onRequestClose={api.close}
      goToStep={api.currentStep}
      getCurrentStep={(i: number) => api.setCurrentStep(i)}
      rounded={10}
      accentColor="#7c3aed"
      maskClassName="tts-mask"
      /* Per-step className variants:
         • `--corner` — pins bubble to bottom-right of viewport on the
           Add Customer modal step (xl modal leaves no side room).
         • `--top` — on steps whose target sits at the BOTTOM of the
           page or sidebar and a bottom-dock bubble would cover it:
             - price          (last field in the sidebar)
             - submit         (footer button)
             - add-entry-week (rendered below the week's table)
           Flipping the bubble to the top edge of the viewport keeps
           the highlighted element visible below it. */
      className={`tts-helper${
        api.steps[api.currentStep]?.key === 'add-customer-modal' ? ' tts-helper--corner' : ''
      }${
        ['price', 'submit', 'add-entry-week'].includes(api.steps[api.currentStep]?.key ?? '') ? ' tts-helper--top' : ''
      }`}
      showButtons={false}
      showNavigation={false}
      showNumber={false}
      /* reactour renders its OWN × close button at the top-right of the
         helper by default (showCloseButton: true). We already render a
         custom × inside <TourBubble>, so disabling this prop removes the
         duplicate that was appearing in the corner. */
      showCloseButton={false}
      closeWithMask={false}
      disableInteraction={false}
      /* `disableFocusLock` — reactour wraps the helper bubble in
         react-focus-lock by default, which traps keyboard focus inside
         the bubble. Whenever the user clicks an input on the page (e.g.
         the Task Details field at Step 4) focus is bounced right back
         to the bubble and typing goes nowhere. Disabling the focus lock
         lets the highlighted form fields receive and keep focus. */
      disableFocusLock={true}
      /* Disable ALL keyboard navigation keys, not just Escape:
           • esc   — was already off so the user can dismiss tooltips
                     inside form controls without closing the tour.
           • right — reactour calls `e.preventDefault()` and advances the
                     step. That broke arrow-key cursor movement inside
                     the Task Details input.
           • left  — same issue, the other direction.
         Navigation still works via the Back / Next / Skip bubble
         buttons; users just can't use arrow keys to step. */
      disableKeyboardNavigation={['esc', 'right', 'left']}
    />}
    </>
  );
};
